import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useNavigate } from 'react-router-dom';
import { getKbTopRegions } from '../lib/api/kb-api';
import './KbTopIndexChart.css';

const VIEW_OPTIONS = [
  { value: 'SALE_IDX', label: '주간 아파트 매매가격지수 상승률 지역 TOP' },
  { value: 'JEONSE_IDX', label: '주간 아파트 전세가격지수 상승률 지역 TOP' },
];

const REGION_SHORT_NAME_MAP = {
  서울특별시: '서울',
  경기도: '경기',
  인천광역시: '인천',
  부산광역시: '부산',
  대전광역시: '대전',
  대구광역시: '대구',
  울산광역시: '울산',
  광주광역시: '광주',
  세종특별자치시: '세종',
  강원특별자치도: '강원',
  충청북도: '충북',
  충청남도: '충남',
  전북특별자치도: '전북',
  전라남도: '전남',
  경상북도: '경북',
  경상남도: '경남',
  제주특별자치도: '제주',
};

const toShortRegionName = (name) => {
  if (!name) return '';
  if (REGION_SHORT_NAME_MAP[name]) return REGION_SHORT_NAME_MAP[name];
  return name;
};

const formatBaseDate = (rawDate) => {
  if (!rawDate) return '-';
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return '-';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

function KbTopIndexChart({ onRegionSelect, regionScopeId, titleRegionName }) {
  const navigate = useNavigate();
  const [indexType, setIndexType] = useState('SALE_IDX');
  const [items, setItems] = useState([]);
  const [baseDate, setBaseDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getKbTopRegions({
          indexType,
          limit: 10,
          regionId: regionScopeId || undefined,
        });
        setItems(Array.isArray(result?.items) ? result.items : []);
        setBaseDate(result?.baseDate || '');
      } catch (e) {
        setItems([]);
        setBaseDate('');
        setError(e?.message || 'KB 지수 데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [indexType, regionScopeId]);

  const chartOption = useMemo(() => {
    const labels = items.map((row) => toShortRegionName(row?.regionNameKo || '-'));
    const values = items.map((row) => Number(row?.value || 0));
    const maxValue = values.length ? Math.max(...values, 0) : 0;

    return {
      animationDuration: 450,
      grid: {
        left: 46,
        right: 18,
        top: 16,
        bottom: 34,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        max: maxValue > 0 ? Number((maxValue * 1.08).toFixed(2)) : undefined,
        splitLine: { lineStyle: { color: '#e7ecf3' } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#4b5d79',
          fontSize: 11,
          formatter: (value) => Number(value).toFixed(2).replace(/\.?0+$/, ''),
        },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: labels,
        axisLabel: {
          color: '#27344b',
          fontSize: 11,
          fontWeight: 600,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar',
          data: values,
          barWidth: 12,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#ffb089' },
                { offset: 1, color: '#ff6b35' },
              ],
            },
            borderRadius: [0, 3, 3, 0],
          },
          label: {
            show: true,
            position: 'right',
            color: '#0f172a',
            fontSize: 11,
            fontWeight: 700,
            formatter: ({ value }) => Number(value).toFixed(2).replace(/\.?0+$/, ''),
          },
        },
      ],
      tooltip: {
        trigger: 'item',
        valueFormatter: (value) => `${Number(value).toFixed(2)}%`,
      },
    };
  }, [items]);

  const viewOptions = useMemo(() => {
    const shortRegionName = toShortRegionName(titleRegionName);
    if (!shortRegionName) {
      return VIEW_OPTIONS;
    }

    return [
      { value: 'SALE_IDX', label: `주간 ${shortRegionName}아파트 매매가격지수 상승률 지역 TOP` },
      { value: 'JEONSE_IDX', label: `주간 ${shortRegionName}아파트 전세가격지수 상승률 지역 TOP` },
    ];
  }, [titleRegionName]);

  const activeViewLabel = useMemo(() => {
    const found = viewOptions.find((option) => option.value === indexType);
    return found?.label || VIEW_OPTIONS[0].label;
  }, [indexType, viewOptions]);

  const chartHeight = 360;
  const dateLabel = formatBaseDate(baseDate);
  const chartEvents = {
    click: (params) => {
      const selected = items?.[params?.dataIndex];
      if (!selected || typeof onRegionSelect !== 'function') {
        return;
      }
      onRegionSelect({
        regionId: selected.regionId,
        regionNameKo: selected.regionNameKo,
      });
    },
  };

  return (
    <section className="kb-top-chart-card">
      <div className="kb-top-chart-header">
        <div className="kb-top-chart-title-wrap compact">
          <div className="kb-top-chart-title-row">
            <span className="kb-top-chart-title-icon" aria-hidden="true"><i className="fas fa-fire" /></span>
            <h3 className="kb-top-chart-title-text">{activeViewLabel}</h3>
          </div>
          <span className="kb-top-chart-date">{dateLabel} 기준</span>
        </div>
        <div className="kb-top-chart-header-right">
          <div className="kb-top-chart-view-toggle">
            <button
              type="button"
              className={indexType === 'SALE_IDX' ? 'active' : ''}
              onClick={() => setIndexType('SALE_IDX')}
            >
              매매
            </button>
            <button
              type="button"
              className={indexType === 'JEONSE_IDX' ? 'active' : ''}
              onClick={() => setIndexType('JEONSE_IDX')}
            >
              전세
            </button>
          </div>
        </div>
      </div>

      {error && <p className="kb-top-chart-error">{error}</p>}

      {!error && loading && <div className="kb-top-chart-state">KB 지수 데이터를 불러오는 중...</div>}
      {!error && !loading && items.length === 0 && <div className="kb-top-chart-state">표시할 데이터가 없습니다.</div>}

      {!error && !loading && items.length > 0 && (
        <ReactECharts
          option={chartOption}
          style={{ width: '100%', height: `${chartHeight}px` }}
          onEvents={chartEvents}
          opts={{ renderer: 'canvas' }}
          notMerge
          lazyUpdate
        />
      )}

      <div className="kb-top-chart-bottom-meta">
        <div className="kb-top-chart-bottom-left">
          <span className="kb-top-chart-unit-text">단위: %</span>
          <span className="kb-top-chart-source">제공 : KB부동산</span>
        </div>
        <button
          type="button"
          className="kb-top-bottom-link"
          onClick={() => navigate(`/kb-data?viewMode=table&indexType=${indexType}&autoSearch=1`)}
        >
          자세히 보기 &gt;
        </button>
      </div>
    </section>
  );
}

export default KbTopIndexChart;
